/**
 * 智能拆分镜对话框（参考电商复刻视觉语言）
 *
 * 流程：
 * 1. 用户输入剧本文本
 * 2. 选模式：按行切分（免费）/ 智能拆分镜（LLM 调用，扣费）
 * 3. 智能拆分镜先返回 ShotProposal[]，让用户预览/编辑/删除
 * 4. 确认后调 applyProposals 批量插入
 */

import { useEffect, useState } from 'react';
import { Loader2, Wand2, X, ScissorsLineDashed, Trash2, Sparkles } from 'lucide-react';
import toast from '../../../../components/Toast';
import { directorApi, DirectorApiError, type ShotProposal } from '../../services/directorApi';

interface Props {
  open: boolean;
  projectId: string;
  onClose: () => void;
  onApplied: () => void;
}

type Stage = 'input' | 'preview';

export function ShotSplitDialog({ open, projectId, onClose, onApplied }: Props) {
  const [stage, setStage] = useState<Stage>('input');
  const [scriptText, setScriptText] = useState('');
  const [proposals, setProposals] = useState<ShotProposal[]>([]);
  const [submitting, setSubmitting] = useState(false);
  /** 智能拆分镜单次扣费（积分），从 publicSettings 拉。null = 还没拿到。 */
  const [llmCost, setLlmCost] = useState<number | null>(null);
  const [llmEnabled, setLlmEnabled] = useState<boolean>(true);

  useEffect(() => {
    if (!open) return;
    directorApi
      .getPublicSettings()
      .then((res) => {
        setLlmCost(res.shotSplitCreditsPerCall);
        setLlmEnabled(res.shotSplitLlmEnabled);
      })
      .catch(() => {
        // 静默：失败不阻塞按钮，仅 fallback 不显示积分
      });
  }, [open]);

  if (!open) return null;

  const handleSplitByLine = async () => {
    if (!scriptText.trim()) {
      toast.error('请填写剧本');
      return;
    }
    setSubmitting(true);
    try {
      const shots = await directorApi.splitByLine(projectId, scriptText);
      toast.success(`已插入 ${shots.length} 个分镜`);
      onApplied();
      handleClose();
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '拆分失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSplitByLLM = async () => {
    if (!scriptText.trim()) {
      toast.error('请填写剧本');
      return;
    }
    if (scriptText.length > 5000) {
      toast.error('剧本不能超过 5000 字');
      return;
    }
    // 扣费二次确认（避免误点）
    if (typeof llmCost === 'number' && llmCost > 0) {
      const ok = window.confirm(
        `智能拆分镜将消耗 ${llmCost} 积分（无论拆出几个分镜，按一次调用扣费）。失败会自动退还。是否继续？`
      );
      if (!ok) return;
    }
    setSubmitting(true);
    try {
      const result = await directorApi.splitScript(projectId, scriptText);
      toast.success(`AI 已生成 ${result.shots.length} 个分镜（消耗 ${result.creditsUsed} 积分）`);
      setProposals(result.shots);
      setStage('preview');
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '智能拆分镜失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApply = async () => {
    if (proposals.length === 0) {
      toast.error('没有可插入的分镜');
      return;
    }
    setSubmitting(true);
    try {
      const shots = await directorApi.applyProposals(projectId, proposals);
      toast.success(`已插入 ${shots.length} 个分镜`);
      onApplied();
      handleClose();
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '应用失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    setStage('input');
    setScriptText('');
    setProposals([]);
    onClose();
  };

  const updateProposal = <K extends keyof ShotProposal>(
    idx: number,
    key: K,
    value: ShotProposal[K]
  ) => {
    setProposals((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [key]: value };
      return next;
    });
  };

  const removeProposal = (idx: number) => {
    setProposals((prev) => prev.filter((_, i) => i !== idx));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-[28px] bg-white shadow-2xl shadow-slate-950/30">
        {/* 顶部 hero */}
        <div className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 p-5 text-white">
          <div className="absolute right-[-40px] top-[-40px] h-32 w-32 rounded-full bg-sky-400/20 blur-2xl" />
          <div className="relative flex items-start justify-between gap-3">
            <div>
              <div className="mb-1.5 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-0.5 text-[11px] font-medium text-slate-200">
                <Sparkles className="h-3 w-3 text-sky-200" />
                {stage === 'input' ? '剧本拆分镜' : '预览结果'}
              </div>
              <h2 className="text-lg font-semibold leading-tight">
                {stage === 'input' ? '从剧本一键生成分镜' : '检查并确认插入'}
              </h2>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="rounded-full bg-white/10 p-1.5 text-white transition hover:bg-white/20"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto bg-slate-50">
          {stage === 'input' && (
            <div className="space-y-4 p-5">
              <div>
                <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">
                  剧本文本（最多 5000 字）
                </label>
                <textarea
                  value={scriptText}
                  onChange={(e) => setScriptText(e.target.value.slice(0, 5000))}
                  rows={12}
                  placeholder={`贴入完整的剧本。例如：\n场景一：女主角推开书院大门，看到男主在练剑。\n\n她愣住了，男主转头看向她。\n\n两人四目相对……`}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 font-mono text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
                />
                <p className="mt-1 text-right text-[11px] text-slate-400">
                  {scriptText.length}/5000
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={handleSplitByLine}
                  disabled={submitting || !scriptText.trim()}
                  className="flex flex-col items-center gap-1 rounded-2xl border border-slate-200 bg-white py-4 text-slate-700 shadow-sm transition hover:border-sky-200 hover:text-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <ScissorsLineDashed className="h-5 w-5" />
                  <span className="text-sm font-semibold">按行切分</span>
                  <span className="text-[10px] text-slate-400">免费 · 简单切割</span>
                </button>
                <button
                  type="button"
                  onClick={handleSplitByLLM}
                  disabled={submitting || !scriptText.trim() || !llmEnabled}
                  title={!llmEnabled ? '管理员未启用智能拆分镜' : undefined}
                  className="flex flex-col items-center gap-1 rounded-2xl bg-slate-950 py-4 text-white shadow-md transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Wand2 className="h-5 w-5 text-sky-200" />
                  <span className="text-sm font-semibold">智能拆分镜</span>
                  <span className="text-[10px] text-slate-300">
                    AI 拆分 ·{' '}
                    {typeof llmCost === 'number'
                      ? llmCost === 0
                        ? '免费'
                        : `${llmCost} 积分/次`
                      : '需扣费'}
                  </span>
                </button>
              </div>

              <div className="rounded-2xl border border-sky-100 bg-sky-50 px-4 py-3 text-xs leading-5 text-sky-900">
                <p>
                  <strong>按行切分</strong>：以双换行为分隔，每段当作一个分镜的提示词，免费插入。
                </p>
                <p className="mt-1">
                  <strong>智能拆分镜</strong>：调用 LLM 自动拆为视频镜头，可建议时长 / 镜头运动 / 出镜角色。
                  {typeof llmCost === 'number' && llmCost > 0 ? (
                    <>
                      单次扣费 <b>{llmCost} 积分</b>，按调用计（无论拆出几个分镜）。失败自动退还。
                    </>
                  ) : (
                    '需扣费。'
                  )}
                </p>
              </div>
            </div>
          )}

          {stage === 'preview' && (
            <div className="space-y-3 p-5">
              <p className="text-sm text-slate-500">
                AI 生成了 {proposals.length} 个分镜建议，可在下面编辑或删除，确认后批量插入到项目末尾。
              </p>
              {proposals.length === 0 ? (
                <div className="py-12 text-center text-slate-500">无可用分镜</div>
              ) : (
                <div className="space-y-3">
                  {proposals.map((p, idx) => (
                    <div
                      key={idx}
                      className="space-y-2 rounded-2xl border border-slate-100 bg-white p-3 shadow-sm"
                    >
                      <div className="flex items-center justify-between">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-sky-50 px-2.5 py-0.5 text-[11px] font-semibold text-sky-700">
                          分镜 #{idx + 1}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeProposal(idx)}
                          className="rounded-full p-1 text-rose-500 transition hover:bg-rose-50"
                          title="删除"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <input
                        value={p.title ?? ''}
                        onChange={(e) => updateProposal(idx, 'title', e.target.value)}
                        placeholder="标题（可选）"
                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-sky-300"
                      />
                      <textarea
                        value={p.prompt}
                        onChange={(e) => updateProposal(idx, 'prompt', e.target.value)}
                        rows={2}
                        placeholder="提示词"
                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-sky-300"
                      />
                      <div className="flex items-center gap-3 text-[11px] text-slate-500">
                        {p.suggestedDuration && <span>{p.suggestedDuration}s</span>}
                        {p.suggestedCameraMovement && (
                          <span>镜头：{p.suggestedCameraMovement}</span>
                        )}
                        {p.suggestedMotionIntensity && (
                          <span>运动：{p.suggestedMotionIntensity}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 bg-white px-5 py-3">
          {stage === 'preview' && (
            <button
              type="button"
              onClick={() => setStage('input')}
              className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              返回
            </button>
          )}
          <button
            type="button"
            onClick={handleClose}
            className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            取消
          </button>
          {stage === 'preview' && (
            <button
              type="button"
              onClick={handleApply}
              disabled={submitting || proposals.length === 0}
              className="inline-flex items-center gap-2 rounded-2xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white shadow-md transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              确认插入 {proposals.length} 个分镜
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
